import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { updateWorkflowStatus } from '../services/workflowService';
import { getWorkflow, updateWorkflowTitle } from '../api/workflowApi';
import type { Workflow, WorkflowEvent, WorkflowDocument } from '../types/workflowTypes';
import { formatCurrency, formatDate } from '../utils/formatters';
import LoadingSpinner from '../components/LoadingSpinner';
import WorkflowPaymentHistoryCard from '../components/WorkflowPaymentHistoryCard';
import DownPaymentModal from '../components/modals/DownPaymentModal';
import { storeFirebaseToken } from '../services/authService';
import { registerPayment } from '../api/workflowApi';
import type { PaymentResponse } from '../api/workflowApi';
import type { DownPayment } from '../types/downPaymentTypes';
import { collection, getDoc, doc, updateDoc, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useIsReadOnly } from '../hooks/useIsReadOnly';
import { INVOICE_FORM_ROUTE } from '../config/cockpitAccess';

// Leeres Export-Statement, um sicherzustellen, dass diese Datei als Modul erkannt wird
export {};

// Hilfsfunktion zur sicheren Erstellung von Date-Objekten
const safeParseDate = (dateString: string | number | Date | null | undefined): Date => {
  try {
    // Versuche, direkt ein Date-Objekt zu erstellen
    const date = new Date(dateString || '');
    // Prüfe, ob das Ergebnis gültig ist
    if (isNaN(date.getTime())) {
      throw new Error('Invalid date value');
    }
    return date;
  } catch (e) {
    console.error('Ungültiges Datum:', dateString, e);
    return new Date(); // Fallback
  }
};

// Hilfsfunktion zur sicheren Erstellung von Date-Objekten mit verschiedenen Eingabetypen
const safeDate = (dateInput: any, fallback = new Date()): Date => {
  if (!dateInput) return fallback;

  try {
    // Falls es ein Firestore Timestamp mit toDate Methode ist
    if (typeof dateInput.toDate === 'function') {
      const date = dateInput.toDate();
      if (date instanceof Date && !isNaN(date.getTime())) {
        return date;
      }
    }

    // Falls es bereits ein Date-Objekt ist
    if (dateInput instanceof Date && !isNaN(dateInput.getTime())) {
      return dateInput;
    }

    // Falls es ein Zeitstempel als Zahl ist
    if (typeof dateInput === 'number') {
      const date = new Date(dateInput);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }

    // Falls es ein String ist, versuche zu parsen
    if (typeof dateInput === 'string') {
      const date = new Date(dateInput);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }

    // Fallback
    console.warn('Konnte Datum nicht sicher konvertieren, verwende Fallback:', dateInput);
    return fallback;
  } catch (error) {
    console.warn('Fehler bei der Datumskonvertierung:', error, dateInput);
    return fallback;
  }
};

// Sichere Formatierung von Datumswerten (delegiert an zentrales formatDate, M-G)
const safeFormatDate = (dateInput: any, includeTime: boolean = false): string => {
  return formatDate(dateInput, { includeTime });
};

// CSS-Styling für die Highlight-Animation
const styles = {
  '@keyframes highlightPulse': {
    '0%': { backgroundColor: 'rgba(79, 70, 229, 0.1)' },
    '50%': { backgroundColor: 'rgba(79, 70, 229, 0.3)' },
    '100%': { backgroundColor: 'rgba(79, 70, 229, 0.1)' }
  },
  highlightAnimation: {
    animation: 'highlightPulse 1.5s ease-in-out'
  }
};

const WorkflowDetailPage: React.FC = () => {
  const { t } = useTranslation('workflow');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  // M11b-3: advisor (read-only) sieht keine mutierenden Dokument-Aktionen
  // (Status-Toggles, Zahlung, Storno, Titel-Edit). Lese-Links (Details/Download) bleiben.
  const isReadOnly = useIsReadOnly();

  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showStatusUpdateForm, setShowStatusUpdateForm] = useState<boolean>(false);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState<boolean>(false);
  const [newStatus, setNewStatus] = useState<string>('');
  const [statusUpdateComment, setStatusUpdateComment] = useState<string>('');
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
  const [selectedInvoice, setSelectedInvoice] = useState<WorkflowDocument | null>(null);
  const [paymentAmount, setPaymentAmount] = useState<string>('');
  const [paymentDate, setPaymentDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [paymentType, setPaymentType] = useState<'full' | 'partial'>('full');
  const [paymentNote, setPaymentNote] = useState<string>('');
  const [paymentLoading, setPaymentLoading] = useState<boolean>(false);
  
  // Down Payment Modal State
  const [showDownPaymentModal, setShowDownPaymentModal] = useState<boolean>(false);
  const [selectedDocumentForDownPayment, setSelectedDocumentForDownPayment] = useState<WorkflowDocument | null>(null);
  const [existingDownPayments, setExistingDownPayments] = useState<DownPayment[]>([]);
  
  // Workflow Title Editing State
  const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
  const [editedTitle, setEditedTitle] = useState<string>('');
  const [isSavingTitle, setIsSavingTitle] = useState<boolean>(false);
  
  // Neue Komponente für Dokumentstatusupdates
  const getDocumentStatusOptions = (documentType: string, currentStatus: string) => {
    // Konvertierung zu lowercase für konsistenten Vergleich
    const type = String(documentType).toLowerCase();
    const status = currentStatus?.toLowerCase() || '';
    
    if (type === 'offer' || type === 'quotation') {
      switch (status) {
        case 'draft':
          return [
            { value: 'sent', label: t('detail.documentActions.markAsSent') }
          ];
        case 'sent':
          return [
            { value: 'accepted', label: t('detail.documentActions.markAsAccepted') },
            { value: 'rejected', label: t('detail.documentActions.markAsRejected') }
          ];
        case 'accepted':
          return [
            { value: 'invoiced', label: t('detail.documentActions.markAsInvoiced') },
            { value: 'rejected', label: t('detail.documentActions.markAsRejectedLater') }
          ];
        case 'rejected':
          return [
            { value: 'draft', label: t('detail.documentActions.resetToDraft') }
          ];
        default:
          return []; // Kein Standard-Status mehr, falls der Status nicht erkannt wird
      }
    } else if (type === 'invoice' || type === 'xrechnung') { // XRechnung hier hinzugefügt
      switch (status) {
        case 'draft':
        case 'created': // Oft ist der initiale Status 'created'
          return [
            { value: 'sent', label: t('detail.documentActions.markAsSent') }
          ];
        case 'sent':
          return [
            { value: 'paid', label: t('detail.documentActions.markAsPaid') },
            { value: 'partially_paid', label: t('detail.documentActions.markAsPartiallyPaid') },
            { value: 'overdue', label: t('detail.documentActions.markAsOverdue') }
          ];
        case 'overdue':
          return [
            { value: 'paid', label: t('detail.documentActions.markAsPaid') },
            { value: 'partially_paid', label: t('detail.documentActions.markAsPartiallyPaid') }
          ];
        case 'partially_paid':
          return [
            { value: 'paid', label: t('detail.documentActions.markAsFullyPaid') }
          ];
        // Fallback für andere Status (z.B. issued, archived), um zumindest 'sent' anzubieten, falls relevant
        // oder leer lassen, wenn keine Aktionen für diese Status gewünscht sind.
        default:
          return [
            // Optional: { value: 'sent', label: t('detail.documentActions.markAsSent') } 
            // oder einfach leer lassen, wenn von 'paid' etc. kein direkter Übergang zu 'sent' möglich sein soll.
          ];
      }
    }
    return [];
  };

  const [documentToUpdate, setDocumentToUpdate] = useState<{ id: string, type: string } | null>(null);
  const [documentNewStatus, setDocumentNewStatus] = useState<string>('');
  const [documentStatusComment, setDocumentStatusComment] = useState<string>('');
  const [documentStatusLoading, setDocumentStatusLoading] = useState<boolean>(false);
  
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      @keyframes highlightPulse {
        0% { background-color: rgba(79, 70, 229, 0.1); }
        50% { background-color: rgba(79, 70, 229, 0.3); }
        100% { background-color: rgba(79, 70, 229, 0.1); }
      }
      .highlight-animation {
        animation: highlightPulse 1.5s ease-in-out;
      }
    `;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  useEffect(() => {
    const loadWorkflow = async () => {
      try {
        if (!id) {
          setError(t('detail.noWorkflowId'));
          setLoading(false);
          return;
        }
        
        // Aktualisiere das Firebase-Token vor API-Anfragen
        await storeFirebaseToken();
        
        if (process.env.NODE_ENV === 'development') {
          console.log(`[WorkflowDetailPage] Lade Workflow mit ID: ${id}`);
        }

        // Direkt die API-Funktion verwenden, nicht den Re-Export
        const response = await getWorkflow(id);
        
        let workflowData: Workflow;
        
        // Prüfen, ob die Antwort im Format {success: true, data: ...} ist
        if (response && 'success' in response && response.success && response.data) {
          workflowData = response.data;
        } 
        // Alternative: Antwort ist direkt das Workflow-Objekt
        else if (response && 'id' in response) {
          workflowData = response as unknown as Workflow;
        }
        // Fehlerfall
        else {
          console.error('Fehler beim Laden des Workflows: Ungültiges Antwortformat', response);
          setError(t('detail.loadingError'));
          setLoading(false);
          return;
        }
        
        // Setze den Workflow
        setWorkflow(workflowData);
        
        if (process.env.NODE_ENV === 'development') {
          console.log(`[WorkflowDetailPage] Workflow geladen:`, workflowData);
        }
        
      } catch (err) {
        console.error('Fehler beim Laden des Workflows:', err);
        setError(t('detail.loadingError'));
      } finally {
        setLoading(false);
      }
    };

    loadWorkflow();
  }, [id]);
  
  // Funktionen für das Inline-Editing des Workflow-Titels
  const handleEditTitleClick = () => {
    setIsEditingTitle(true);
    setEditedTitle(workflow?.name || '');
  };

  const handleSaveTitle = async () => {
    if (!workflow || editedTitle.trim() === workflow.name) {
      setIsEditingTitle(false);
      return;
    }

    setIsSavingTitle(true);
    try {
      await updateWorkflowTitle(workflow.id, editedTitle.trim());
      setIsEditingTitle(false);
      // Lade den Workflow neu, um die Änderungen zu reflektieren
      await reloadWorkflow();
    } catch (error) {
      console.error('Fehler beim Speichern des Workflow-Titels:', error);
      // Hier könnte man eine Toast-Notification oder ähnliches zeigen
    } finally {
      setIsSavingTitle(false);
    }
  };

  const handleCancelEditTitle = () => {
    setIsEditingTitle(false);
    setEditedTitle(workflow?.name || '');
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveTitle();
    } else if (e.key === 'Escape') {
      handleCancelEditTitle();
    }
  };

  const handleStatusUpdate = async () => {
    try {
      setStatusUpdateLoading(true);
      setError(null);
      
      if (!id || !newStatus) {
        setError('Workflow-ID oder neuer Status fehlt');
        return;
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`[WorkflowDetailPage] Status-Update gestartet: ${id} -> ${newStatus}`);
      }
      
      const updatedWorkflow = await updateWorkflowStatus(id, newStatus, statusUpdateComment);
      
      if (updatedWorkflow) {
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ [WorkflowDetailPage] Status-Update erfolgreich durchgeführt');
        }
        
        setShowStatusUpdateForm(false);
        setNewStatus('');
        setStatusUpdateComment('');
        
        // Nach erfolgreicher Aktualisierung den Workflow neu laden
        const response = await getWorkflow(id);
        
        let workflowData: Workflow;
        
        // Prüfen, ob die Antwort im Format {success: true, data: ...} ist
        if (response && 'success' in response && response.success && response.data) {
          workflowData = response.data;
        } 
        // Alternative: Antwort ist direkt das Workflow-Objekt
        else if (response && 'id' in response) {
          workflowData = response as unknown as Workflow;
        }
        // Fehlerfall
        else {
          console.error('[WorkflowDetailPage] Status-Update fehlgeschlagen - ungültiges Antwortformat', response);
          setError('Status konnte nicht aktualisiert werden.');
          return;
        }
        
        setWorkflow(workflowData);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('📋 [WorkflowDetailPage] Workflow nach Status-Update aktualisiert:', workflowData.status);
        }
      } else {
        console.error('[WorkflowDetailPage] Status-Update fehlgeschlagen - kein aktualisierter Workflow erhalten');
        setError('Status konnte nicht aktualisiert werden.');
      }
    } catch (err) {
      console.error('[WorkflowDetailPage] Fehler beim Aktualisieren des Status:', err);
      setError('Status konnte nicht aktualisiert werden.');
    } finally {
      setStatusUpdateLoading(false);
    }
  };
  
  const getNextPossibleStatuses = () => {
    if (!workflow) return [];
    
    switch (workflow.status) {
      case 'draft':
        return [
          { value: 'sent', label: t('detail.documentActions.markAsSent') }
        ];
      case 'sent':
        return [
          { value: 'accepted', label: t('detail.documentActions.markAsAccepted') },
          { value: 'rejected', label: t('detail.documentActions.markAsRejected') }
        ];
      case 'accepted':
        return [
          { value: 'invoiced', label: t('detail.documentActions.markAsInvoiced') }
        ];
      case 'invoiced':
        return [
          { value: 'paid', label: t('detail.documentActions.markAsPaid') },
          { value: 'overdue', label: t('detail.documentActions.markAsOverdue') }
        ];
      case 'overdue':
        return [
          { value: 'paid', label: t('detail.documentActions.markAsPaid') }
        ];
      default:
        return [];
    }
  };
  
  const getDocumentIcon = (document: WorkflowDocument) => {
    switch (document.type) {
      case 'offer':
        return '📄';
      case 'invoice':
        return '💼';
      case 'xrechnung': // Hinzugefügt für XRechnung
        return '🧾'; // Icon für XRechnung (z.B. eine andere Art von Dokument)
      case 'down_payment':
        return '💵'; // Icon für Abschlagsrechnung
      case 'final_invoice':
        return '🏁'; // Icon für Abschlussrechnung
      case 'reminder':
        return '⏰';
      case 'credit_note':
        return '💰';
      default:
        return '📝'; // Standard-Icon
    }
  };
  
  const getDocumentTypeName = (document: WorkflowDocument) => {
    if (String(document.type).toLowerCase() === 'quotation') {
      return t('detail.documentTypes.quotation'); // Behandelt 'quotation' als 'Angebot'
    }
    
    switch (document.type) {
      case 'offer':
        return t('detail.documentTypes.offer');
      case 'invoice':
        return t('detail.documentTypes.invoice');
      case 'xrechnung': 
        return t('detail.documentTypes.xrechnung');
      case 'down_payment':
        return t('detail.documentTypes.downPayment');
      case 'final_invoice':
        return t('detail.documentTypes.finalInvoice');
      case 'reminder':
        return t('detail.documentTypes.reminder');
      case 'credit_note':
        return t('detail.documentTypes.creditNote');
      default:
        // Explizites Casting, um dem Linter zu helfen
        const unhandledType = document.type as string;
        return unhandledType ? unhandledType.charAt(0).toUpperCase() + unhandledType.slice(1) : t('detail.documentTypes.document');
    }
  };
  
  const getStatusClass = (status?: string) => {
    if (!status) return 'bg-surface-hover text-secondary';
    
    switch (status) {
      case 'draft': return 'bg-surface-hover text-secondary';
      case 'sent': return 'bg-[var(--tone-info-bg)] text-[var(--tone-info-fg)] border border-[var(--tone-info-border)]';
      case 'accepted': return 'bg-[var(--tone-success-bg)] text-[var(--tone-success-fg)] border border-[var(--tone-success-border)]';
      case 'invoiced': return 'bg-[var(--tone-accent-bg)] text-[var(--tone-accent-fg)] border border-[var(--tone-accent-border)]';
      case 'paid': return 'bg-green-500 text-white';
      case 'overdue': return 'bg-[var(--tone-danger-bg)] text-[var(--tone-danger-fg)] border border-[var(--tone-danger-border)]';
      case 'rejected': return 'bg-red-500 text-white';
      case 'canceled': return 'bg-gray-500 text-primary';
      default: return 'bg-surface-hover text-primary';
    }
  };
  
  const getStatusTranslation = (status?: string): string => {
    if (!status) return t('card.status.unknown');
    
    // Lowercase für konsistenten Vergleich
    const statusLower = status.toLowerCase();
    
    switch (statusLower) {
      case 'draft': return t('card.status.draft');
      case 'sent': return t('card.status.sent');
      case 'accepted': return t('card.status.accepted');
      case 'invoiced': return t('card.status.invoiced');
      case 'paid': return t('card.status.paid');
      case 'overdue': return t('card.status.overdue');
      case 'rejected': return t('card.status.rejected');
      case 'canceled': return t('card.status.canceled');
      case 'issued': return t('card.status.issued');
      case 'created': return t('card.status.created');
      case 'unknown': return t('card.status.unknown');
      default: return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };
  
  const getEventTypeName = (event: WorkflowEvent): string => {
    const specificActionMap: Record<string, string> = {
      'status_change': t('detail.eventTypes.status_changed'),
      'add_reminder': t('detail.eventTypes.add_reminder'),
      'add_credit_note': t('detail.eventTypes.add_credit_note'),
      'payment_registered': `💰 ${t('detail.eventTypes.payment_registered')}`,
      'payment': `💰 ${t('detail.eventTypes.payment_registered')}`,
      'document_revision': t('detail.eventTypes.document_revision'),
      'revision': t('detail.eventTypes.revision_created'),
      'offer_created_with_workflow': t('detail.eventTypes.offer_created'),
      'invoice_created_with_workflow': t('detail.eventTypes.invoice_created'),
      'xrechnung_created': t('detail.eventTypes.xrechnung_created'),
      'down_payment_created_with_workflow': `💵 ${t('detail.eventTypes.down_payment_created')}`,
      'final_invoice_created_with_workflow': `🏁 ${t('detail.eventTypes.final_invoice_created')}`
    };

    if (specificActionMap[event.action]) {
        return specificActionMap[event.action];
    }

    // Generische Aktionen interpretieren
    if (event.action === 'create') {
        // Spezieller Check für Rechnungen
        if (event.entityType === 'invoice') return t('detail.eventTypes.invoice_created');
        if (event.details?.documentType === 'invoice') return t('detail.eventTypes.invoice_created');
        if (event.details?.documentType === 'offer') return t('detail.eventTypes.offer_created');
        if (event.details?.targetType === 'workflow_reference') return t('detail.eventTypes.workflow_created');
        if (event.details?.customerName) return t('detail.eventTypes.workflow_for_customer', { customer: event.details.customerName });
        // Bessere Fallbacks für Dokumente
        if (event.details?.documentId) return t('detail.eventTypes.document_created');
        if (event.details?.type === 'offer') return t('detail.eventTypes.offer_created');
        if (event.details?.type === 'invoice') return t('detail.eventTypes.invoice_created');
        return t('detail.eventTypes.workflow_created'); // Verbesserte Standardbeschreibung
    }
    
    if (event.action === 'update') {
        if (event.details?.targetType === 'workflow_reference') return t('detail.eventTypes.workflow_updated');
        if (event.details?.status) return t('detail.eventTypes.status_changed_to', { status: event.details.status });
        return t('detail.eventTypes.updated'); // Besserer Fallback
    }
    
    // Fallback für unbekannte Aktionen
    return typeof event.action === 'string' ? event.action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : t('detail.eventTypes.unknown_action');
  };
  
  const handleRegisterPaymentClick = (document: WorkflowDocument) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[WorkflowDetailPage] Öffne Zahlungsmodal für:', document.number);
    }
    
    // Berechne bereits bezahlten Betrag und Restbetrag
    const relatedPayments = workflow?.payments?.filter(p => p.documentId === document.id) || [];
    const totalPaidSoFar = relatedPayments.reduce((sum, payment) => sum + payment.amount, 0);
    
    // Extra sorgfältige Berechnung und Rundung des Restbetrags
    const exactRemainingAmount = document.totalAmount - totalPaidSoFar;
    // Erst auf 4 Stellen runden, dann auf 2 Stellen, um Rundungsfehler zu eliminieren
    const roundedRemaining = Math.round(Math.round(exactRemainingAmount * 10000) / 100) / 100;
    const remainingAmount = Math.max(0, roundedRemaining);
    
    // Debug-Ausgabe für Rundungsprobleme
    if (process.env.NODE_ENV === 'development') {
      console.log('[WorkflowDetailPage] Zahlungsdetails berechnet:');
      console.log('- Rechnungsbetrag:', document.totalAmount);
      console.log('- Bereits bezahlt:', totalPaidSoFar);
      console.log('- Exakter Restbetrag:', exactRemainingAmount);
      console.log('- Gerundeter Restbetrag:', remainingAmount);
    }
    
    setSelectedInvoice(document);
    setPaymentType('full');
    
    // Setze den Betrag auf den gerundeten Wert mit exakt 2 Dezimalstellen
    setPaymentAmount(remainingAmount.toFixed(2));
    
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setPaymentNote('');
    setShowPaymentModal(true);
  };
  
  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedInvoice(null);
    setPaymentAmount('');
    setPaymentNote('');
  };

  // Down Payment Modal Funktionen
  const handleDownPaymentClick = async (document: WorkflowDocument) => {
    console.log('[WorkflowDetailPage] ========== HANDLE DOWN PAYMENT CLICK ==========');
    console.log('[WorkflowDetailPage] Document:', document);
    console.log('[WorkflowDetailPage] Workflow:', workflow);
    console.log('[WorkflowDetailPage] CurrentUser exists:', !!currentUser);
    
    setSelectedDocumentForDownPayment(document);
    
    // Existierende Abschlagsrechnungen laden
    if (workflow?.id && currentUser) {
      try {
        console.log('[WorkflowDetailPage] ✅ Loading existing down payments for workflow:', workflow.id);
        const token = await currentUser.getIdToken();
        console.log('[WorkflowDetailPage] ✅ Token obtained, calling API...');
        const { getExistingDownPayments } = await import('../api/downPaymentApi');
        console.log('[WorkflowDetailPage] ✅ Making API call to getExistingDownPayments...');
        const existingDownPayments = await getExistingDownPayments(workflow.id, token);
        console.log('[WorkflowDetailPage] ✅ API response - existing down payments:', existingDownPayments);
        console.log('[WorkflowDetailPage] ✅ Number of existing down payments:', existingDownPayments.length);
        setExistingDownPayments(existingDownPayments);
        if (existingDownPayments.length > 0) {
          console.log('[WorkflowDetailPage] ✅ Found existing down payments, setting state...');
        } else {
          console.log('[WorkflowDetailPage] ⚠️ No existing down payments found.');
        }
      } catch (error) {
        console.error('[WorkflowDetailPage] ❌ Failed to load existing down payments:', error);
        console.error('[WorkflowDetailPage] ❌ Error stack:', error instanceof Error ? error.stack : 'No stack');
        setExistingDownPayments([]); // Fallback zu leerem Array
      }
    } else {
      console.log('[WorkflowDetailPage] ❌ Cannot load down payments - workflow.id:', workflow?.id, 'currentUser exists:', !!currentUser);
      setExistingDownPayments([]);
    }
    
    console.log('[WorkflowDetailPage] 🚀 Opening DownPaymentModal...');
    setShowDownPaymentModal(true);
  };

  const closeDownPaymentModal = () => {
    setShowDownPaymentModal(false);
    setSelectedDocumentForDownPayment(null);
    setExistingDownPayments([]);
  };

  const handleDownPaymentCreated = async (newDownPayment: DownPayment) => {
    try {
      // Füge die neue Abschlagsrechnung zu den existierenden hinzu
      setExistingDownPayments(prev => [...prev, newDownPayment]);
      
      // Workflow neu laden, um die Änderungen zu reflektieren
      await reloadWorkflow();
      
      // Modal schließen
      closeDownPaymentModal();
    } catch (error) {
      console.error('Fehler beim Verarbeiten der neuen Abschlagsrechnung:', error);
    }
  };
  
  const handlePaymentSuccess = async (result: PaymentResponse) => {
    setPaymentLoading(false);
    setShowPaymentModal(false);
    setPaymentAmount('');
    setPaymentNote('');
    
    // Zeige Erfolgsmeldung
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ [WorkflowDetailPage] Zahlung erfolgreich registriert:', result);
    }
    
    // Lade den Workflow nach 1 Sekunde neu, um die Aktualisierung anzuzeigen
    if (id) {
      setTimeout(async () => {
        try {
          const response = await getWorkflow(id);
          
          let workflowData: Workflow;
          
          // Prüfen, ob die Antwort im Format {success: true, data: ...} ist
          if (response && 'success' in response && response.success && response.data) {
            workflowData = response.data;
          } 
          // Alternative: Antwort ist direkt das Workflow-Objekt
          else if (response && 'id' in response) {
            workflowData = response as unknown as Workflow;
          }
          // Fehlerfall
          else {
            console.error('[WorkflowDetailPage] Konnte Workflow nach Zahlungs-Update nicht laden - ungültiges Antwortformat', response);
            return;
          }
          
          setWorkflow(workflowData);
          if (process.env.NODE_ENV === 'development') {
            console.log('📋 [WorkflowDetailPage] Workflow nach Zahlungs-Update neu geladen:', workflowData.status);
          }
        } catch (reloadErr) {
          console.error('Fehler beim Neuladen des Workflows nach Zahlung:', reloadErr);
        }
      }, 1000);
    }
  };
  
  const confirmPayment = async () => {
    if (!id || !selectedInvoice) return;
    
    try {
      setPaymentLoading(true);
      setError(null); // Fehler zurücksetzen
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[WorkflowDetailPage] Zahlung wird verarbeitet...');
      }
      
      await storeFirebaseToken(); // Token aktualisieren
      
      const newStatus = paymentType === 'full' ? 'paid' : 'partially_paid';
      const paymentAmountNumber = parseFloat(paymentAmount);
      
      // Berechne den noch offenen Betrag nach dieser Zahlung
      // 1. Sammle bisherige Zahlungen für diese Rechnung
      const existingPayments = workflow?.payments?.filter(p => p.documentId === selectedInvoice.id) || [];
      const totalPaidSoFar = existingPayments.reduce((sum, payment) => sum + payment.amount, 0);
      const totalAfterThisPayment = totalPaidSoFar + paymentAmountNumber;
      
      // Extra sorgfältige Berechnung und Rundung des Restbetrags
      const exactRemainingAmount = selectedInvoice.totalAmount - totalAfterThisPayment;
      // Erst auf 4 Stellen runden, dann auf 2 Stellen, um Rundungsfehler zu eliminieren
      const roundedRemaining = Math.round(Math.round(exactRemainingAmount * 10000) / 100) / 100;
      const remainingAmount = Math.max(0, roundedRemaining);
      
      // Debug-Ausgabe für Rundungsprobleme
      if (process.env.NODE_ENV === 'development') {
        console.log('[WorkflowDetailPage][ConfirmPayment] Zahlungsdetails berechnet:');
        console.log('- Rechnungsbetrag:', selectedInvoice.totalAmount);
        console.log('- Bisherige Zahlungen:', totalPaidSoFar);
        console.log('- Aktuelle Zahlung:', paymentAmountNumber);
        console.log('- Gesamt bezahlt nach dieser Zahlung:', totalAfterThisPayment);
        console.log('- Exakter Restbetrag:', exactRemainingAmount);
        console.log('- Gerundeter Restbetrag:', remainingAmount);
      }
      
      const paymentDetails = {
        documentId: selectedInvoice.id,
        documentNumber: selectedInvoice.number,
        amount: paymentAmountNumber,
        currency: selectedInvoice.currency,
        paymentDate: paymentDate,
        paymentType: paymentType as 'full' | 'partial',
        paymentMethod: 'manual',
        note: paymentNote
      };
      
      // Erstelle einen aussagekräftigeren Kommentar, der den verbleibenden Betrag enthält
      const paymentTypeText = paymentType === 'full' ? t('detail.payment.paymentRegisteredFull') : t('detail.payment.paymentRegisteredPartial');
      let comment = t('detail.payment.paymentRegisteredComment', { type: paymentTypeText }) + `: ${paymentAmountNumber} ${selectedInvoice.currency}`;

      // Explizit den bereits gezahlten Betrag hinzufügen, wenn es Vorhandene gibt
      if (totalPaidSoFar > 0) {
        comment += ` (${t('detail.payment.alreadyPaidBefore')}: ${totalPaidSoFar.toFixed(2)} ${selectedInvoice.currency})`;
      }
      
      if (paymentType === 'partial' && roundedRemaining > 0) {
        comment += ` - ${t('detail.payment.stillOpen')}: ${roundedRemaining.toFixed(2)} ${selectedInvoice.currency}`;
      }
      
      if (paymentNote) {
        comment += `. ${t('detail.payment.noteLabel')}: ${paymentNote}`;
      }
      
      // Erweitere die Zahlungsdetails um den verbleibenden Betrag für die Anzeige im Verlauf
      // Wir fügen alle relevanten Informationen hinzu, damit sie direkt sichtbar sind
      const extendedDetails = {
        ...paymentDetails,
        // Verbleibender Betrag explizit hinzufügen
        remainingAmount: roundedRemaining,
        // Totalbeträge für zusätzlichen Kontext 
        totalPaidSoFar,
        totalAfterThisPayment,
        originalAmount: selectedInvoice.totalAmount,
        // Der Kommentar enthält wichtige Informationen und wird als Fallback verwendet
        fullComment: comment
      };
      
      // Direkter API Aufruf
      const response = await registerPayment(
        id,
        newStatus,
        extendedDetails,
        comment
      );
      
      // Verbesserte Erfolgsprüfung:
      // 1. Entweder `success` Feld ist true
      // 2. ODER die Antwort enthält eine Erfolgs-String-Nachricht
      const isSuccessResponse = 
        (response && typeof response === 'object' && 'success' in response && response.success) ||
        (typeof response === 'string' && (response as string).toLowerCase().includes('success')) ||
        (response && typeof response === 'object' && 'message' in response && 
         typeof response.message === 'string' && response.message.toLowerCase().includes('success'));
      
      if (isSuccessResponse) {
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ [WorkflowDetailPage] Zahlung erfolgreich über API registriert');
        }
        
        // Zusätzlich aktualisieren wir direkt den Rechnungsstatus in Firestore
        try {
          if (selectedInvoice.id) {
            // Rechnung in Firestore aktualisieren
            const invoiceRef = doc(db, 'invoices', selectedInvoice.id);
            
            // Status-Update für die Rechnung
            await updateDoc(invoiceRef, {
              status: newStatus, // 'paid' oder 'partially_paid'
              updatedAt: Timestamp.now(),
              // Weitere relevante Felder bei Bedarf
            });
            
            if (process.env.NODE_ENV === 'development') {
              console.log(`✅ [WorkflowDetailPage] Rechnungsstatus für ${selectedInvoice.id} direkt in Firestore aktualisiert zu "${newStatus}"`);
            }
          }
        } catch (updateError) {
          // Bei Fehlern loggen wir sie, lassen den Vorgang aber durchlaufen
          console.error('[WorkflowDetailPage] Fehler beim direkten Aktualisieren des Rechnungsstatus:', updateError);
        }
        
        // Erfolgreiche Zahlung verarbeiten
        handlePaymentSuccess(response);
      } else {
        const errorMessage = 
          (typeof response === 'object' && 'message' in response) ? response.message : 
          (typeof response === 'string' ? response : 'Unbekannter Fehler vom Server');
        
        console.error('[WorkflowDetailPage] API meldet Fehler bei Zahlungsregistrierung:', errorMessage);
        setError(`Zahlung konnte nicht registriert werden: ${errorMessage}`);
        setPaymentLoading(false);
      }
    } catch (err: any) {
      console.error('[WorkflowDetailPage] Fehler bei der Zahlungsregistrierung (API-Aufruf fehlgeschlagen):', err);
      const status = err.response?.status;
      const message = err.response?.data?.error || err.message || 'Netzwerkfehler';
      setError(`Fehler (${status || 'Client'}): ${message}. Bitte später erneut versuchen.`);
      setPaymentLoading(false);
    }
  };
  
  const handlePaymentTypeChange = (type: 'full' | 'partial') => {
    setPaymentType(type);
    if (type === 'full' && selectedInvoice) {
      // Berechne den noch offenen Restbetrag
      const relatedPayments = workflow?.payments?.filter(p => p.documentId === selectedInvoice.id) || [];
      const totalPaidSoFar = relatedPayments.reduce((sum, payment) => sum + payment.amount, 0);
      
      // Extra sorgfältige Berechnung und Rundung des Restbetrags
      const exactRemainingAmount = selectedInvoice.totalAmount - totalPaidSoFar;
      // Erst auf 4 Stellen runden, dann auf 2 Stellen, um Rundungsfehler zu eliminieren
      const roundedRemaining = Math.round(Math.round(exactRemainingAmount * 10000) / 100) / 100;
      const remainingAmount = Math.max(0, roundedRemaining);
      
      // Debug-Ausgabe für Rundungsprobleme
      if (process.env.NODE_ENV === 'development') {
        console.log('[WorkflowDetailPage][PaymentTypeChange] Zahlungsdetails berechnet:');
        console.log('- Rechnungsbetrag:', selectedInvoice.totalAmount);
        console.log('- Bereits bezahlt:', totalPaidSoFar);
        console.log('- Exakter Restbetrag:', exactRemainingAmount);
        console.log('- Gerundeter Restbetrag:', remainingAmount);
      }
      
      // Setze den Betrag auf den gerundeten Wert mit exakt 2 Dezimalstellen
      setPaymentAmount(remainingAmount.toFixed(2));
    } else if (type === 'partial') {
      setPaymentAmount('');
    }
  };
  
  const handleUpdateDocumentStatus = async () => {
    if (!id || !documentToUpdate || !documentNewStatus) return;
    
    try {
      setDocumentStatusLoading(true);
      setError(null);
      
      const comment = documentStatusComment || 
        `${getDocumentTypeName({ type: documentToUpdate.type } as WorkflowDocument)} ${getStatusTranslation(documentNewStatus)}`;
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`[WorkflowDetailPage] Dokument-Status-Update für ${documentToUpdate.id}: ${documentNewStatus}, Kommentar: ${comment}`);
      }
      
      // Finde das Dokument-Objekt aus der Workflow-Liste
      const documentObj = workflow?.documents?.find(doc => doc.id === documentToUpdate.id);
      
      // Dokument-Details für die Statusänderung vorbereiten
      const documentDetails = {
        documentId: documentToUpdate.id,
        documentType: documentToUpdate.type,
        documentNumber: documentObj?.number || t('detail.common.unknown'),
        oldStatus: documentObj?.status || 'unknown',
        status: documentNewStatus,
        comment: comment
      };
      
      // Workflow Status über API aktualisieren mit Dokument-Details
      const updatedWorkflow = await updateWorkflowStatus(
        id, 
        documentNewStatus, 
        documentDetails
      );
      
      if (updatedWorkflow) {
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ [WorkflowDetailPage] Dokument-Status-Update erfolgreich durchgeführt');
        }
        
        setDocumentToUpdate(null);
        setDocumentNewStatus('');
        setDocumentStatusComment('');
        
        // Workflow neu laden
        reloadWorkflow();
        
      } else {
        console.error('[WorkflowDetailPage] Dokument-Status-Update fehlgeschlagen - kein aktualisierter Workflow erhalten');
        setError('Status konnte nicht aktualisiert werden.');
      }
    } catch (err) {
      console.error('[WorkflowDetailPage] Fehler beim Aktualisieren des Dokument-Status:', err);
      setError('Status konnte nicht aktualisiert werden.');
    } finally {
      setDocumentStatusLoading(false);
    }
  };
  
  // Hilfs-Funktion zum Neuladen des Workflows
  const reloadWorkflow = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      
      const response = await getWorkflow(id);
      
      let workflowData: Workflow;
      
      // Prüfen, ob die Antwort im Format {success: true, data: ...} ist
      if (response && 'success' in response && response.success && response.data) {
        workflowData = response.data;
      } 
      // Alternative: Antwort ist direkt das Workflow-Objekt
      else if (response && 'id' in response) {
        workflowData = response as unknown as Workflow;
      }
      // Fehlerfall
      else {
        console.error('[WorkflowDetailPage] Konnte Workflow nicht neu laden - ungültiges Antwortformat', response);
        return;
      }
      
      // Workflow aktualisieren
      setWorkflow(workflowData);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('📋 [WorkflowDetailPage] Workflow neu geladen:', workflowData);
      }
    } catch (error) {
      console.error('[WorkflowDetailPage] Fehler beim Neuladen des Workflows:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Event-Handler für Status-Toggles
  const handleStatusToggleClick = (documentId: string, documentType: string, newStatus: string, comment: string) => {
    console.log(`[WorkflowDetailPage] Toggle geklickt: ${documentId}, Status ändern zu '${newStatus}'`);
    setDocumentToUpdate({ id: documentId, type: documentType });
    setDocumentNewStatus(newStatus);
    setDocumentStatusComment(comment);
  };
  
  const getDocumentActions = (document: WorkflowDocument) => {
    // Status-Optionen für dieses Dokument
    const docType = String(document.type).toLowerCase();
    // Mit explizitem Typ-Cast, um TypeScript zu zeigen, dass alle Status-Werte möglich sind
    const currentStatus = String(document.status || '').toLowerCase();

    // DEBUG: Log document info
    console.log('[WorkflowDetailPage] Document Debug:', {
      id: document.id,
      type: document.type,
      docType: docType,
      status: document.status,
      currentStatus: currentStatus,
      number: document.number
    });

    // Hilfsfunktion, um TypeScript-Vergleichsprobleme zu umgehen
    const statusIs = (status: string): boolean => {
      return currentStatus === status;
    };
    
    // Hilfsfunktion für Negation
    const statusIsNot = (status: string): boolean => {
      return currentStatus !== status;
    };
    
    return (
      <>
        {/* Mutierende Dokument-Aktionen — M11b-3: für advisor (read-only) ausgeblendet */}
        {!isReadOnly && (
        <>
        {/* Hauptaktions-Buttons */}
        {(docType === 'offer' || docType === 'quotation') && String(workflow?.status || '').toLowerCase() === 'accepted' && (
          /* Root Cause wie beim Storno-Button unten (Support-Ticket Irene Bayer,
             2026-08-01; Reviewer-Finding F-1, Re-Loop 1): dieser Link navigierte
             bisher auf den bloßen `/dashboard`-Index. Seit der GA-Freischaltung
             des Owner-Cockpits (REACT_APP_UX_COCKPIT_ENABLED=true, 2026-07-26)
             rendert der Index für ALLE Nutzer `CockpitPage` statt
             `InvoiceDashboard` — der `offerId`-Query-Param (gelesen in
             `InvoiceDashboard.tsx:276`) landete in der URL, wurde aber nie
             ausgelesen. Fix: IMMER `INVOICE_FORM_ROUTE` verwenden (die echte,
             flag-freie Konstante aus `architecture-cockpit-route-split.md`
             §3 E2) statt eines hartkodierten `/dashboard`-Literals. */
          <Link
            to={`${INVOICE_FORM_ROUTE}?offerId=${document.id}`}
            className="text-xs sm:text-sm bg-[var(--tone-accent-bg)] text-[var(--tone-accent-fg)] px-2 py-1 rounded hover:brightness-110 transition-colors whitespace-nowrap"
          >
            {t('detail.createInvoice')}
          </Link>
        )}
        
        {/* Zahlung registrieren */}
        {(docType === 'invoice' || docType === 'xrechnung' || docType === 'down_payment' || docType === 'final_invoice') && currentStatus !== 'paid' && currentStatus !== 'fully_paid' && (
          <button
            onClick={() => handleRegisterPaymentClick(document)}
            className="text-xs sm:text-sm bg-[var(--tone-success-bg)] text-[var(--tone-success-fg)] px-2 py-1 rounded hover:brightness-110 transition-colors whitespace-nowrap"
          >
            {t('detail.registerPayment')}
          </button>
        )}

        {/* Versendet-Button (nur für Entwürfe) */}
        {(docType === 'offer' || docType === 'quotation' || docType === 'invoice' || docType === 'xrechnung' || docType === 'down_payment' || docType === 'final_invoice') && 
         (statusIs('draft') || statusIs('created')) && (
          <button
            onClick={() => {
              handleStatusToggleClick(
                document.id, 
                document.type, 
                'sent', 
                `${getDocumentTypeName(document)} ${t('detail.statusComments.markedAsSent')}`
              );
            }}
            className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm font-medium rounded transition whitespace-nowrap"
          >
            {t('detail.sent')}
          </button>
        )}
        
        {/* Angenommen-Button (für versendete oder abgelehnte Angebote) */}
        {(docType === 'offer' || docType === 'quotation') && 
         (statusIs('sent') || statusIs('rejected')) && (
          <button
            onClick={() => {
              handleStatusToggleClick(
                document.id, 
                document.type, 
                'accepted', 
                `${getDocumentTypeName(document)} ${t('detail.statusComments.markedAsAccepted')}`
              );
            }}
            className="px-2 py-1 text-xs rounded-md bg-[var(--tone-success-bg)] text-[var(--tone-success-fg)] hover:brightness-110 transition-colors whitespace-nowrap"
          >
            {t('detail.accepted')}
          </button>
        )}
        
        {/* Abgelehnt-Button (für versendete oder angenommene Angebote) */}
        {(docType === 'offer' || docType === 'quotation') && 
         (statusIs('sent') || statusIs('accepted')) && (
          <button
            onClick={() => {
              handleStatusToggleClick(
                document.id, 
                document.type, 
                'rejected', 
                `${getDocumentTypeName(document)} ${t('detail.statusComments.wasRejected')}`
              );
            }}
            className="px-2 py-1 text-xs rounded-md bg-[var(--tone-danger-bg)] text-[var(--tone-danger-fg)] hover:brightness-110 transition-colors whitespace-nowrap"
          >
            {t('detail.rejected')}
          </button>
        )}
        
        {/* Überfällig-Button (nur für versendete Rechnungen) */}
        {(docType === 'invoice' || docType === 'xrechnung' || docType === 'down_payment' || docType === 'final_invoice') && statusIs('sent') && docType !== 'xrechnung' && (
          <button
            onClick={() => {
              handleStatusToggleClick(
                document.id, 
                document.type, 
                'overdue', 
                `${getDocumentTypeName(document)} ${t('detail.statusComments.markedAsOverdue')}`
              );
            }}
            className="px-2 py-1 text-xs rounded-md bg-[var(--tone-danger-bg)] text-[var(--tone-danger-fg)] hover:brightness-110 transition-colors whitespace-nowrap"
          >
            {t('detail.overdue')}
          </button>
        )}
        
        {/* Bezahlt-Button (für versendete/überfällige Rechnungen) */}
        {(docType === 'invoice' || docType === 'xrechnung' || docType === 'down_payment' || docType === 'final_invoice') && (statusIs('sent') || statusIs('overdue')) && docType !== 'xrechnung' && (
          <button
            onClick={() => handleRegisterPaymentClick(document)}
            className="px-2 py-1 text-xs rounded-md bg-[var(--tone-success-bg)] text-[var(--tone-success-fg)] hover:brightness-110 transition-colors whitespace-nowrap"
          >
            {t('detail.paid')}
          </button>
        )}

        {/* ⭐ NEU: Storno-Button für Rechnungen */}
        {(docType === 'invoice' || docType === 'xrechnung' || docType === 'down_payment' || docType === 'final_invoice') && 
         !document.isCancelled && !document.isCancellation && (
          <button
            onClick={() => {
              if (!document.number) {
                alert(t('cancellation.errors.missingInvoiceNumber', 'Rechnungsnummer fehlt'));
                return;
              }
              // Root Cause (Support-Ticket Irene Bayer, 2026-08-01): dieser Call navigierte
              // bisher auf den bloßen `/dashboard`-Index. Seit der GA-Freischaltung des
              // Owner-Cockpits (REACT_APP_UX_COCKPIT_ENABLED=true, 2026-07-26) rendert der
              // Index für ALLE Nutzer `CockpitPage` statt `InvoiceDashboard` — die
              // `cancellationFor`/`invoiceNumber`-Query-Params landeten in der URL, wurden
              // aber nie ausgelesen (CockpitPage verarbeitet sie nicht). Für die Nutzerin sah
              // das wie "Klick tut nichts" aus. Fix: IMMER `INVOICE_FORM_ROUTE` verwenden
              // (die echte, flag-freie, garantiert auf `InvoiceDashboard` zeigende Konstante aus
              // `architecture-cockpit-route-split.md` §3 E2) statt eines hartkodierten
              // `/dashboard`-Literals.
              navigate(`${INVOICE_FORM_ROUTE}?cancellationFor=${document.id}&invoiceNumber=${encodeURIComponent(document.number)}`);
            }}
            className="text-xs sm:text-sm bg-[var(--tone-danger-bg)] text-[var(--tone-danger-fg)] px-2 py-1 rounded hover:brightness-110 transition-colors whitespace-nowrap"
            title={t('cancellation.button.tooltip', 'Stornorechnung erstellen')}
          >
            {t('cancellation.button.label', 'Storno')}
          </button>
        )}
        </>
        )}

        {/* Hinweis wenn storniert */}
        {document.isCancelled && (
          <span className="text-xs text-[var(--tone-danger-fg)] italic px-2 py-1">
            {t('cancellation.status.cancelled', 'Storniert')}
          </span>
        )}
        
        {/* Hinweis wenn Stornorechnung */}
        {document.isCancellation && (
          <span className="text-xs text-[var(--tone-caution-fg)] italic px-2 py-1">
            {t('cancellation.status.isCancellation', 'Stornorechnung')}
          </span>
        )}

        {/* Details anzeigen */}
        <Link
          to={`/${(document.type === 'invoice' || document.type === 'xrechnung' || document.type === 'down_payment' || document.type === 'final_invoice') ? 'dashboard/archiv' : 'dashboard/angebote-archiv'}?id=${document.id}`}
          className="text-xs sm:text-sm bg-surface-inset text-secondary px-2 py-1 rounded hover:bg-surface-hover transition-colors whitespace-nowrap"
                                  title={t('detail.showDetails')}
                      >
                        {t('detail.details')}
        </Link>
      </>
    );
  };
  
  if (loading) {
    return (
      <div className="bg-surface text-primary min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-surface text-primary min-h-screen">
        <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          <div className="bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] text-[var(--tone-danger-fg)] p-4 my-4 rounded-lg">
            <p><strong>{t('detail.common.error')}:</strong> {error}</p>
          </div>
          <button 
            onClick={() => navigate('/dashboard/workflows')}
            className="mt-4 px-4 py-2 bg-surface-raised text-secondary rounded-md hover:bg-surface-inset transition-colors"
          >
            {t('detail.backToOverview')}
          </button>
        </div>
      </div>
    );
  }
  
  if (!workflow) {
    return (
      <div className="bg-surface text-primary min-h-screen">
        <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          <div className="bg-surface-raised rounded-xl p-8 text-center border border-hairline">
                            <h3 className="text-xl font-medium text-secondary mb-2">{t('detail.workflowNotFound')}</h3>
            <p className="text-muted mb-6">
                              {t('detail.workflowNotFoundMessage')}
            </p>
            <button 
              onClick={() => navigate('/dashboard/workflows')}
              className="px-4 py-2 bg-indigo-600 text-[#0C1322] rounded-md hover:bg-indigo-700 transition-colors"
            >
              {t('detail.backToOverview')}
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Workflow-Titel (Primär: workflow.name, Fallback: Kundendaten)
  const workflowTitle = (() => {
    // Primäre Quelle: workflow.name (neues System)
    if (workflow.name) return workflow.name;
    
    // Fallback: Kundenname (altes System)
    if (workflow.customer?.companyName) return workflow.customer.companyName;
    if (workflow.customer?.personName) return workflow.customer.personName;
    
    // Alternative Quelle: metadata.customer
    if (workflow.metadata?.customer?.companyName) return workflow.metadata.customer.companyName;
    if (workflow.metadata?.customer?.name) return workflow.metadata.customer.name;
    if (workflow.metadata?.customer?.personName) return workflow.metadata.customer.personName;
    
    // Dritte Alternative: metadata.customerName
    if (workflow.metadata?.customerName) return workflow.metadata.customerName;
    
    // Fallback
    return t('detail.common.unnamedWorkflow');
  })();
  
  const startDate = safeFormatDate(workflow.createdAt);
  const lastUpdate = safeFormatDate(workflow.updatedAt || workflow.createdAt);
  const documents = workflow.documents || [];
  const events = workflow.events || [];
  
  // DEBUG: Events-Array in der Konsole anzeigen
  console.log("[WorkflowDetailPage DEBUG] Events in Workflow:", events);
  
  // Sichere Berechnung des Gesamtwerts (Logs entfernt)
  const totalValue = (() => {
    try {
      if (!documents || documents.length === 0) return 0;
      const safeExtractAmount = (doc: WorkflowDocument): number => {
        if (!doc || doc.totalAmount === undefined || doc.totalAmount === null) return 0;
        if (typeof doc.totalAmount === 'number' && !isNaN(doc.totalAmount)) return doc.totalAmount;
        if (typeof doc.totalAmount === 'string') {
          // Explicit type assertion to string
          const cleanAmount = (doc.totalAmount as string).replace(/[^\d.,]/g, '').replace(',', '.');
          const parsedAmount = parseFloat(cleanAmount);
          if (!isNaN(parsedAmount)) return parsedAmount;
          console.warn(`[WFDetail] Konnte String-Betrag nicht konvertieren: "${doc.totalAmount}"`);
        } else {
          console.warn(`[WFDetail] Unerwarteter totalAmount-Typ:`, typeof doc.totalAmount, doc.totalAmount);
        }
        return 0;
      };
      // Berücksichtige Rechnungen, Angebote und XRechnungen für den Gesamtwert
      const relevantDocuments = documents.filter(
        doc => doc.type === 'invoice' || doc.type === 'offer' || doc.type === 'xrechnung'
      );
      if (relevantDocuments.length > 0) {
        return relevantDocuments.reduce((sum, doc) => sum + safeExtractAmount(doc), 0);
      }
      return 0;
    } catch (error) {
      console.error('[WFDetail] Fehler bei der Berechnung des Gesamtwerts:', error);
      return 0;
    }
  })();

  // Main return for when workflow is loaded and no error
  return (
    <div className="bg-surface text-primary min-h-screen">
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Zurück-Navigation */}
        <div className="mb-6">
          <Link 
            to="/dashboard/workflows" 
            className="text-accent-ink hover:text-accent-ink flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd"></path>
            </svg>
            {t('detail.backToOverview')}
          </Link>
        </div>
        
        {/* Header mit Workflow-Infos */}
        <div className="bg-surface-raised shadow-lg rounded-xl p-6 mb-6 border border-hairline">
          <div className="flex flex-col lg:flex-row justify-between items-start mb-4 gap-4">
            <div>
              <div className="flex items-center group">
                {isEditingTitle ? (
                  <div className="flex-1 flex items-center space-x-2">
                    <input
                      type="text"
                      value={editedTitle}
                      onChange={(e) => setEditedTitle(e.target.value)}
                      onKeyDown={handleTitleKeyDown}
                      className="flex-1 bg-surface-inset text-primary text-2xl font-bold px-2 py-1 rounded border border-strong focus:border-indigo-500 focus:outline-none"
                      autoFocus
                      disabled={isSavingTitle}
                    />
                    <button
                      onClick={handleSaveTitle}
                      disabled={isSavingTitle}
                      className="text-[var(--tone-success-fg)] hover:text-green-300 text-sm font-medium disabled:opacity-50"
                    >
                      {isSavingTitle ? '...' : '✓'}
                    </button>
                    <button
                      onClick={handleCancelEditTitle}
                      disabled={isSavingTitle}
                      className="text-muted hover:text-secondary text-sm font-medium disabled:opacity-50"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <>
                    <h1 className="text-2xl font-bold text-primary mb-2 flex-1" title={workflowTitle}>
                      {workflowTitle}
                    </h1>
                    {/* Titel bearbeiten — M11b-3: für advisor (read-only) ausgeblendet */}
                    {!isReadOnly && (
                    <button
                      onClick={handleEditTitleClick}
                      className="opacity-0 group-hover:opacity-100 text-muted hover:text-primary text-sm ml-2 transition-opacity"
                      title={t('detail.editTitle')}
                    >
                      ✏️
                    </button>
                    )}
                  </>
                )}
              </div>
              <p className="text-muted">
                {t('detail.businessProcess')} #{workflow.id.substring(0, 8)}
              </p>
            </div>
            <div className="lg:text-right">
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${getStatusClass(workflow.status)}`}>
                {workflow.status ? getStatusTranslation(workflow.status) : t('detail.common.unknown')}
              </span>
              <p className="text-muted mt-2">
                {startDate} {t('detail.to')} {lastUpdate}
              </p>
              {totalValue > 0 && (
                <p className="font-semibold mt-2 text-primary">
                  {formatCurrency(totalValue, 'EUR')}
                </p>
              )}
            </div>
          </div>
          
          {/* Statusaktualisierung - wird entfernt */}
          {/* Früherer Block für globale Statusaktualisierung entfernt */}
        </div>
        
        {/* Dokumente */}
        <div className="bg-surface-raised shadow-lg rounded-xl p-6 mb-6 border border-hairline">
          <h2 className="text-xl font-semibold mb-4 text-primary">{t('detail.documents')}</h2>
          {documents.length === 0 ? (
            <p className="text-muted">{t('detail.noDocuments')}</p>
          ) : (
            <div className="space-y-4 divide-y divide-[color:var(--border-hairline)]">
              {documents.map((document, index) => (
                <div key={document.id || index} className={`${index > 0 ? 'pt-4' : ''}`}>
                  {/* Mobile/Desktop responsive Layout */}
                  <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start space-y-3 lg:space-y-0">
                    {/* Dokumentinfo und Icons */}
                    <div className="flex items-center lg:flex-1">
                      <span className="text-2xl mr-3 flex-shrink-0">{getDocumentIcon(document)}</span>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-medium text-primary truncate">
                          {getDocumentTypeName(document)} #{document.number || 'N/A'}
                        </h3>
                        <p className="text-sm text-muted">
                          {safeFormatDate(document.date)} - <span className={`px-1.5 py-0.5 text-xs rounded ${getStatusClass(document.status)}`}>{getStatusTranslation(document.status) || t('detail.common.unknown')}</span>
                        </p>
                      </div>
                    </div>
                    
                    {/* Betrag - nur auf großen Bildschirmen rechts */}
                    <div className="hidden lg:block flex-shrink-0 lg:text-right">
                      <span className="font-medium text-primary text-lg lg:text-base">
                        {formatCurrency(document.totalAmount, document.currency)}
                      </span>
                    </div>
                  </div>

                  {/* Zahlungsinformationen und Actions - kombiniert für bessere Nutzung der Höhe */}
                  {(document.type === 'invoice' || document.type === 'xrechnung' || document.type === 'down_payment' || document.type === 'final_invoice') && 
                   (document.status === 'partially_paid' || document.status === 'paid' || document.status === 'fully_paid') ? (
                    <div className="mt-4 lg:mt-3">
                      {/* Kleiner Bildschirm: Betrag und Info Box */}
                      <div className="lg:hidden">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-3">
                          {/* Betrag auf kleinen Bildschirmen */}
                          <div className="flex-shrink-0">
                            <span className="font-medium text-primary text-lg">
                              {formatCurrency(document.totalAmount, document.currency)}
                            </span>
                          </div>
                          
                          {/* Info Box */}
                          <div className="bg-surface-inset rounded-lg p-3 border-l-4 border-yellow-500 flex-1">
                            {(() => {
                              // Berechne den noch offenen Betrag
                              const relatedPayments = workflow?.payments?.filter(p => p.documentId === document.id) || [];
                              const totalPaid = relatedPayments.reduce((sum, payment) => sum + payment.amount, 0);
                              const remainingAmount = Math.max(0, document.totalAmount - totalPaid);
                              const paymentPercentage = document.totalAmount > 0 ? (totalPaid / document.totalAmount) * 100 : 0;
                              
                              return (
                                <div className="space-y-2">
                                  {/* Fortschrittsbalken */}
                                  <div className="w-full bg-surface-hover rounded-full h-2">
                                    <div 
                                      className={`h-2 rounded-full transition-all duration-300 ${
                                        paymentPercentage >= 100 ? 'bg-green-500' : 'bg-yellow-500'
                                      }`}
                                      style={{ width: `${Math.min(paymentPercentage, 100)}%` }}
                                    ></div>
                                  </div>
                                  
                                  {/* Zahlungsdetails */}
                                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center text-sm space-y-1 sm:space-y-0">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-1 sm:space-y-0">
                                      <span className="text-[var(--tone-success-fg)] font-medium">
                                        ✓ {formatCurrency(totalPaid, document.currency)}
                                      </span>
                                      {remainingAmount > 0 && (
                                        <span className="text-[var(--tone-warning-fg)] font-medium">
                                          ⏳ {formatCurrency(remainingAmount, document.currency)}
                                        </span>
                                      )}
                                    </div>
                                    <span className="text-secondary text-xs">
                                      {Math.round(paymentPercentage)}% bezahlt
                                    </span>
                                  </div>
                                  
                                                                  {/* Anzahl der Zahlungen */}
                                {relatedPayments.length > 0 && (
                                  <div className="text-xs text-muted">
                                    {relatedPayments.length} {relatedPayments.length === 1 ? t('detail.payment.paymentsRegistered') : t('detail.payment.paymentsRegisteredPlural')}
                                  </div>
                                )}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </div>

                      {/* Großer Bildschirm: Info Box links, Buttons rechts in derselben Reihe */}
                      <div className="hidden lg:flex lg:justify-between lg:items-start gap-6">
                        {/* Info Box */}
                        <div className="bg-surface-inset rounded-lg p-3 border-l-4 border-yellow-500 max-w-md flex-1">
                          {(() => {
                            // Berechne den noch offenen Betrag
                            const relatedPayments = workflow?.payments?.filter(p => p.documentId === document.id) || [];
                            const totalPaid = relatedPayments.reduce((sum, payment) => sum + payment.amount, 0);
                            const remainingAmount = Math.max(0, document.totalAmount - totalPaid);
                            const paymentPercentage = document.totalAmount > 0 ? (totalPaid / document.totalAmount) * 100 : 0;
                            
                            return (
                              <div className="space-y-2">
                                {/* Fortschrittsbalken */}
                                <div className="w-full bg-surface-hover rounded-full h-2">
                                  <div 
                                    className={`h-2 rounded-full transition-all duration-300 ${
                                      paymentPercentage >= 100 ? 'bg-green-500' : 'bg-yellow-500'
                                    }`}
                                    style={{ width: `${Math.min(paymentPercentage, 100)}%` }}
                                  ></div>
                                </div>
                                
                                {/* Zahlungsdetails */}
                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center text-sm space-y-1 sm:space-y-0">
                                  <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-1 sm:space-y-0">
                                    <span className="text-[var(--tone-success-fg)] font-medium">
                                      ✓ {formatCurrency(totalPaid, document.currency)}
                                    </span>
                                    {remainingAmount > 0 && (
                                      <span className="text-[var(--tone-warning-fg)] font-medium">
                                        ⏳ {formatCurrency(remainingAmount, document.currency)}
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-secondary text-xs">
                                    {Math.round(paymentPercentage)}{t('detail.payment.percentPaid')}
                                  </span>
                                </div>
                                
                                {/* Anzahl der Zahlungen */}
                                {relatedPayments.length > 0 && (
                                  <div className="text-xs text-muted">
                                    {relatedPayments.length} {relatedPayments.length === 1 ? t('detail.payment.paymentsRegistered') : t('detail.payment.paymentsRegisteredPlural')}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>

                        {/* Actions auf großen Bildschirmen */}
                        <div className="flex flex-wrap gap-2 justify-end flex-shrink-0">
                          {getDocumentActions(document)}
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Actions für Dokumente ohne Zahlungsinfo */
                    <div className="pt-3 flex flex-wrap gap-2 justify-start lg:justify-end">
                      {getDocumentActions(document)}
                    </div>
                  )}

                  {/* Actions nur auf kleinen Bildschirmen anzeigen, wenn Zahlungsinfo vorhanden */}
                  {(document.type === 'invoice' || document.type === 'xrechnung' || document.type === 'down_payment' || document.type === 'final_invoice') && 
                   (document.status === 'partially_paid' || document.status === 'paid' || document.status === 'fully_paid') && (
                    <div className="lg:hidden pt-3 flex flex-wrap gap-2 justify-start">
                      {getDocumentActions(document)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Zeitleiste */}
        <div className="bg-surface-raised shadow-lg rounded-xl p-6 border border-hairline">
                          <h2 className="text-xl font-semibold mb-6 text-primary">{t('detail.timeline')}</h2>
          
          {!events || events.length === 0 ? (
            <p className="text-muted">{t('detail.timelineEmpty')}</p>
          ) : (
            <div className="relative ml-6">
              <div className="absolute h-full w-0.5 bg-surface-hover left-0"></div>
              <div className="space-y-8">
                {events
                  .slice()
                  .sort((a, b) => safeDate(b.timestamp).getTime() - safeDate(a.timestamp).getTime())
                  .map((event, index) => (
                    <div key={index} className="relative pl-8">
                      <div className={`absolute -left-3 mt-1 w-6 h-6 rounded-full flex items-center justify-center text-xs
                        ${(event.action === 'payment_registered' || event.action === 'payment') ?
                          ((event.details?.payment?.type || event.details?.type || event.details?.paymentType) === 'full' ?
                            'bg-green-600 text-white' : 'bg-yellow-600 text-white') :
                          'bg-indigo-600 text-[#0C1322]'}`
                      }>
                        {event.action === 'create' || 
                         event.action === 'offer_created_with_workflow' || 
                         event.action === 'invoice_created_with_workflow' ? '🌟' :
                         event.action === 'status_change' ? '🔄' :
                         (typeof event.action === 'string' && event.action.startsWith('add_')) ? '➕' :
                         (event.action === 'payment_registered' || event.action === 'payment') ? '💰' : '📝'}
                      </div>
                      <div className="bg-surface-inset rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="font-medium text-primary">
                            {(event.action === 'payment_registered' || event.action === 'payment') ? (
                              <span className="text-[var(--tone-warning-fg)] font-bold">{t('detail.eventTypes.payment_registered')}</span>
                            ) : getEventTypeName(event)}
                          </h3>
                          <p className="text-sm text-muted">{safeFormatDate(event.timestamp, true)}</p>
                        </div>
                        
                        {/* Extra Info für Zahlungen, die immer angezeigt wird */}
                        {(event.action === 'payment_registered' || event.action === 'payment') && (
                          <div className="mt-1 mb-2 bg-surface-raised p-2 rounded flex items-center">
                            <span className="text-sm mr-2 text-secondary">Betrag:</span>
                            <span className="font-bold text-primary">
                              {formatCurrency(
                                // Versuche verschiedene Wege, den Betrag zu ermitteln
                                event.details?.payment?.amount || 
                                event.details?.amount || 
                                event.details?.paymentDetails?.amount ||
                                0, 
                                // Versuche verschiedene Wege, die Währung zu ermitteln
                                event.details?.payment?.currency || 
                                event.details?.currency || 
                                event.details?.paymentDetails?.currency ||
                                'EUR'
                              )}
                            </span>
                          </div>
                        )}
                        
                        {/* Status-Änderungs-Events */}
                        {event.action === 'status_change' && event.details && (
                          <div className="mt-2 space-y-1">
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-0.5 text-xs rounded ${getStatusClass(String(event.details.oldStatus || ''))}`}>
                                {getStatusTranslation(String(event.details.oldStatus || ''))}
                              </span>
                              <svg className="w-4 h-4 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                              </svg>
                              <span className={`px-2 py-0.5 text-xs rounded ${getStatusClass(String(event.details.newStatus || ''))}`}>
                                {getStatusTranslation(String(event.details.newStatus || ''))}
                              </span>
                            </div>
                            {event.details.comment && <p className="mt-1 text-secondary text-sm bg-surface-raised p-2 rounded-md border-l-2 border-indigo-500">"{event.details.comment}"</p>}
                            <p className="text-muted text-xs">
                              {event.userName && event.userName !== "System" ? `${t('detail.userActions.performedBy')} ${event.userName}` : ''}
                            </p>
                          </div>
                        )}
                        
                        {/* Alternative Bezeichnung für Status-Update */}
                        {event.action === 'update_status' && event.details && (
                          <div className="mt-2 space-y-1">
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-0.5 text-xs rounded ${getStatusClass(String(event.details.oldStatus || ''))}`}>
                                {getStatusTranslation(String(event.details.oldStatus || ''))}
                              </span>
                              <svg className="w-4 h-4 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                              </svg>
                              <span className={`px-2 py-0.5 text-xs rounded ${getStatusClass(String(event.details.newStatus || ''))}`}>
                                {getStatusTranslation(String(event.details.newStatus || ''))}
                              </span>
                            </div>
                            {event.details.comment && <p className="mt-1 text-secondary text-sm bg-surface-raised p-2 rounded-md border-l-2 border-indigo-500">"{event.details.comment}"</p>}
                            <p className="text-muted text-xs">
                              {event.userName && event.userName !== "System" ? `${t('detail.userActions.performedBy')} ${event.userName}` : ''}
                            </p>
                          </div>
                        )}
                        
                        {/* Dokument hinzugefügt */}
                        {(typeof event.action === 'string' && event.action.startsWith('add_')) && event.details && (
                          <div className="mt-2 text-secondary">
                            <p>{getDocumentTypeName({ type: event.details.documentType } as WorkflowDocument)} {t('detail.documentEvents.added')}</p>
                            {event.details.documentId && <p className="text-xs text-muted mt-1">{t('detail.documentEvents.documentId')}: {event.details.documentId}</p>}
                            {event.userName && event.userName !== "System" && (
                              <p className="text-muted text-xs mt-1">{t('detail.userActions.createdBy')} {event.userName}</p>
                            )}
                          </div>
                        )}
                        
                        {/* Angebot erstellt */}
                        {event.action === 'offer_created_with_workflow' && event.details && (
                          <div className="mt-2 text-secondary">
                            <p>
                              {t('detail.documentTypes.offer')} <span className="font-semibold">{event.details.offerNumber || ''}</span> {t('detail.eventTypes.created')}
                              {event.details.amount && event.details.currency && 
                                <span> ({formatCurrency(parseFloat(event.details.amount as string), event.details.currency as string)})</span>
                              }
                            </p>
                            <p className="text-xs text-muted mt-1">
                              {t('detail.workflowStage')}: {getDocumentTypeName({ type: event.details.workflowStageType } as WorkflowDocument)} ({getStatusTranslation(event.details.workflowStageStatus)})
                            </p>
                            {event.userName && event.userName !== "System" && (
                              <p className="text-muted text-xs mt-1">{t('detail.createdBy')} {event.userName}</p>
                            )}
                          </div>
                        )}
                        
                        {/* Rechnung erstellt (mit Workflow) */}
                        {event.action === 'invoice_created_with_workflow' && event.details && (
                          <div className="mt-2 text-secondary">
                            <p>
                              {t('detail.documentTypes.invoice')} <span className="font-semibold">{event.details.invoiceNumber || ''}</span> {t('detail.eventTypes.created')}
                              {event.details.amount && event.details.currency && 
                                <span> ({formatCurrency(parseFloat(event.details.amount as string), event.details.currency as string)})</span>
                              }
                            </p>
                            <p className="text-xs text-muted mt-1">
                              {t('detail.workflowStage')}: {t('detail.documentTypes.invoice')} ({getStatusTranslation(event.details.workflowStageStatus || 'issued')})
                            </p>
                            {event.userName && event.userName !== "System" && (
                              <p className="text-muted text-xs mt-1">{t('detail.userActions.createdBy')} {event.userName}</p>
                            )}
                          </div>
                        )}
                        
                        {/* Rechnung erstellt (einfach) */}
                        {event.action === 'create' && event.entityType === 'invoice' && event.details && (
                          <div className="mt-2 text-secondary">
                            <p>
                              {t('detail.documentTypes.invoice')} <span className="font-semibold">{event.details.invoiceNumber || ''}</span> {t('detail.eventTypes.created')}
                              {event.details.amount && event.details.currency && 
                                <span> ({formatCurrency(parseFloat(event.details.amount as string), event.details.currency as string)})</span>
                              }
                            </p>
                            {event.userName && event.userName !== "System" && (
                              <p className="text-muted text-xs mt-1">{t('detail.userActions.createdBy')} {event.userName}</p>
                            )}
                          </div>
                        )}
                        
                        {/* Geschäftsvorgang erstellt */}
                        {event.action === 'create' && !event.entityType && event.details && (
                          <div className="mt-2 text-secondary">
                            <p>{t('detail.eventTypes.workflow_created')}</p>
                                                          {event.details?.status && <p className="text-xs text-muted mt-1">{t('detail.initialStatus')}: <span className={`px-1.5 py-0.5 rounded ${getStatusClass(event.details.status)}`}>{getStatusTranslation(event.details.status)}</span></p>}
                            {event.userName && event.userName !== "System" && (
                              <p className="text-muted text-xs mt-1">{t('detail.userActions.createdBy')} {event.userName}</p>
                            )}
                          </div>
                        )}
                        
                        {/* Zahlung registriert - Verbesserte Implementierung mit mehr Fallbacks */}
                        {(event.action === 'payment_registered' || event.action === 'payment') && (
                          <div className="mt-2 text-secondary">
                            {/* Debug-Ausgabe in Entwicklungsumgebung entfernen */}
                            {/* Flexible Zugriffe für verschiedene Datenstrukturen */}
                            {(() => {
                              // Verschiedene Möglichkeiten, wo die Zahlungsdaten sein könnten
                              const paymentDetails = 
                                event.details?.payment || 
                                event.details?.paymentDetails || 
                                event.details || 
                                {};
                                
                              // Extrahiere Werte mit Fallbacks
                              const paymentType = 
                                paymentDetails.type || 
                                paymentDetails.paymentType ||
                                event.details?.type || 
                                event.details?.paymentType ||
                                'partial';
                                
                              const paymentAmount = 
                                paymentDetails.amount || 
                                event.details?.amount || 
                                0;
                                
                              const paymentCurrency = 
                                paymentDetails.currency || 
                                event.details?.currency || 
                                'EUR';
                                
                              const paymentDate = 
                                paymentDetails.date || 
                                paymentDetails.paymentDate || 
                                event.details?.date || 
                                event.details?.paymentDate ||
                                event.timestamp;
                                
                              const paymentNote = 
                                paymentDetails.note || 
                                paymentDetails.notes || 
                                event.details?.note || 
                                event.details?.comment;
                                
                              // Verschiedene Möglichkeiten, den Dokumentennamen zu finden
                              const documentNumber = 
                                event.details?.documentNumber || 
                                paymentDetails.documentNumber;
                                
                              // Verschiedene Möglichkeiten, den verbleibenden Betrag zu erhalten
                              const remainingAmount = 
                                event.details?.remainingAmount !== undefined ? event.details.remainingAmount :
                                paymentDetails.remainingAmount !== undefined ? paymentDetails.remainingAmount :
                                null;
                                
                              return (
                                <>
                                  <div className="flex items-center">
                                                        <span className={`px-2 py-0.5 text-xs rounded ${paymentType === 'full' ? 'bg-[var(--tone-success-bg)] text-[var(--tone-success-fg)]' : 'bg-[var(--tone-warning-bg)] text-[var(--tone-warning-fg)]'}`}>
                      {paymentType === 'full' ? t('detail.payment.paymentTypeFull') : t('detail.payment.paymentTypePartial')}
                    </span>
                                    <span className="ml-2 font-medium">
                                      {formatCurrency(paymentAmount, paymentCurrency)}
                                    </span>
                                  </div>
                                  
                                  {paymentDate && (
                                    <p className="text-sm text-muted mt-1">
                                      {t('detail.payment.date')}: {safeFormatDate(paymentDate)}
                                    </p>
                                  )}
                                  
                                  {documentNumber && (
                                    <p className="text-sm text-muted mt-1">
                                      {t('detail.documentTypes.document')}: {documentNumber}
                                    </p>
                                  )}
                                  
                                  {paymentNote && (
                                    <p className="mt-1 text-secondary text-sm bg-surface-raised p-2 rounded-md border-l-2 border-yellow-500">
                                      "{paymentNote}"
                                    </p>
                                  )}
                                  
                                  {/* Alle Kommentare durchsuchen, ob darin ein Hinweis auf offenen Betrag steht */}
                                                          {!remainingAmount && paymentNote && paymentNote.includes(t('detail.payment.stillOpen')) && (
                          <p className="text-sm text-[var(--tone-warning-fg)] mt-1">
                            {paymentNote.substring(paymentNote.indexOf(t('detail.payment.stillOpen')))}
                          </p>
                        )}
                                  
                                  {remainingAmount !== null && (
                                    <p className="text-sm text-muted mt-1">
                                      {t('detail.payment.remainingAmount')}: <span className={`font-medium ${parseFloat(String(remainingAmount)) > 0 ? 'text-[var(--tone-danger-fg)]' : 'text-[var(--tone-success-fg)]'}`}>
                                        {formatCurrency(remainingAmount, paymentCurrency)}
                                      </span>
                                    </p>
                                  )}
                                  
                                  <p className="text-muted text-xs mt-1">
                                    {event.userName && event.userName !== "System" ? `${t('detail.userActions.registeredBy')} ${event.userName}` : ''}
                                  </p>
                                </>
                              );
                            })()}
                          </div>
                        )}
                        
                        {/* XRechnung erstellt (NEU) */}
                        {event.action === 'xrechnung_created' && event.details && (
                          <div className="mt-2 text-secondary">
                            <p>
                              XRechnung <span className="font-semibold">{event.details.invoiceNumber || ''}</span> {t('detail.documentEvents.created')}
                              {event.details.totalAmount && event.details.currency && 
                                <span> ({formatCurrency(parseFloat(event.details.totalAmount as string), event.details.currency as string)})</span>
                              }
                            </p>
                            <p className="text-xs text-muted mt-1">
                              {t('detail.status')}: {getStatusTranslation(event.details.status || 'issued')}
                            </p>
                            {event.userName && event.userName !== "System" && (
                              <p className="text-muted text-xs mt-1">{t('detail.userActions.createdBy')} {event.userName}</p>
                            )}
                          </div>
                        )}
                        
                        {/* Abschlagsrechnung erstellt */}
                        {event.action === 'down_payment_created_with_workflow' && event.details && (
                          <div className="mt-2 text-secondary">
                            <p>
                              Abschlagsrechnung <span className="font-semibold">{event.details.invoiceNumber || ''}</span> {t('detail.documentEvents.created')}
                              {(event.details.amount || event.details.formattedAmount) && (
                                <span> ({event.details.formattedAmount || formatCurrency(parseFloat(event.details.amount as string), event.details.currency as string || 'EUR')})</span>
                              )}
                            </p>
                            <p className="text-xs text-muted mt-1">
                              {t('detail.workflowStage')}: {t('detail.documentTypes.invoice')} ({getStatusTranslation('issued')})
                            </p>
                            {event.userName && event.userName !== "System" && (
                              <p className="text-muted text-xs mt-1">{t('detail.userActions.createdBy')} {event.userName}</p>
                            )}
                          </div>
                        )}
                        
                        {/* Abschlussrechnung erstellt */}
                        {event.action === 'final_invoice_created_with_workflow' && event.details && (
                          <div className="mt-2 text-secondary">
                            <p>
                              {t('detail.documentTypes.finalInvoice')} <span className="font-semibold">{event.details.invoiceNumber || ''}</span> {t('detail.documentEvents.created')}
                              {(event.details.amount || event.details.formattedAmount) && (
                                <span> ({event.details.formattedAmount || formatCurrency(parseFloat(event.details.amount as string), event.details.currency as string || 'EUR')})</span>
                              )}
                            </p>
                            <p className="text-xs text-muted mt-1">
                              {t('detail.workflowStage')}: {t('detail.documentTypes.invoice')} ({getStatusTranslation('issued')})
                            </p>
                            {event.userName && event.userName !== "System" && (
                              <p className="text-muted text-xs mt-1">{t('detail.userActions.createdBy')} {event.userName}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Zahlungshistorie-Komponente */}
        {workflow.payments && workflow.payments.length > 0 && (
          <WorkflowPaymentHistoryCard 
            payments={workflow.payments} 
            currency={workflow.latestDocument?.currency || 'EUR'}
            totalAmount={workflow.latestDocument?.totalAmount}
          />
        )}

        {/* Zahlungs-Modal */}
        {showPaymentModal && selectedInvoice && (
          <div className="fixed inset-0 bg-surface bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-surface-raised rounded-lg shadow-xl border border-hairline w-full max-w-md sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h3 className="text-xl font-semibold text-primary mb-4">
                  {t('detail.payment.title')}
                </h3>
                
                <div className="mb-6">
                  <p className="text-secondary mb-1">
                    {t('detail.payment.invoice')}: <span className="font-medium text-primary">{selectedInvoice.number}</span>
                  </p>
                  <p className="text-secondary mb-3">
                    {t('detail.payment.totalAmount')}: <span className="font-medium text-primary">
                      {formatCurrency(selectedInvoice.totalAmount, selectedInvoice.currency)}
                    </span>
                  </p>
                  
                  {/* Zahlungsstatus und verbleibender Betrag */}
                  {(() => {
                    const relatedPayments = workflow?.payments?.filter(p => p.documentId === selectedInvoice.id) || [];
                    const totalPaid = relatedPayments.reduce((sum, payment) => sum + payment.amount, 0);
                    const remainingAmount = Math.max(0, selectedInvoice.totalAmount - totalPaid);
                    const paymentPercentage = selectedInvoice.totalAmount > 0 ? (totalPaid / selectedInvoice.totalAmount) * 100 : 0;
                    
                    if (relatedPayments.length > 0) {
                      return (
                        <div className="bg-surface-inset rounded-lg p-4 border-l-4 border-blue-500 mb-4">
                          <h4 className="text-sm font-medium text-primary mb-3">💰 {t('detail.payment.paymentStatus')}</h4>
                          
                          {/* Fortschrittsbalken */}
                          <div className="w-full bg-surface-hover rounded-full h-2 mb-3">
                            <div 
                              className={`h-2 rounded-full transition-all duration-300 ${
                                paymentPercentage >= 100 ? 'bg-green-500' : 'bg-yellow-500'
                              }`}
                              style={{ width: `${Math.min(paymentPercentage, 100)}%` }}
                            ></div>
                          </div>
                          
                          {/* Zahlungsdetails */}
                          <div className="flex justify-between items-center text-sm mb-2">
                            <div className="flex items-center space-x-4">
                              <span className="text-[var(--tone-success-fg)] font-medium">
                                ✓ {formatCurrency(totalPaid, selectedInvoice.currency)}
                              </span>
                              {remainingAmount > 0 && (
                                <span className="text-[var(--tone-warning-fg)] font-medium">
                                  ⏳ {formatCurrency(remainingAmount, selectedInvoice.currency)}
                                </span>
                              )}
                            </div>
                            <span className="text-secondary text-xs">
                              {Math.round(paymentPercentage)}{t('detail.payment.percentPaid')}
                            </span>
                          </div>
                          
                          {/* Anzahl der Zahlungen */}
                          <div className="text-xs text-muted">
                            {relatedPayments.length} {relatedPayments.length === 1 ? t('detail.payment.paymentsRegistered') : t('detail.payment.paymentsRegisteredPlural')} {t('detail.payment.alreadyRegistered')}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
                
                {/* Zahlungstyp */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-secondary mb-2">
                    {t('detail.payment.type')}
                  </label>
                  <div className="flex space-x-4">
                    <button
                      type="button"
                      onClick={() => handlePaymentTypeChange('full')}
                      className={`flex-1 py-2 px-4 rounded-md ${
                        paymentType === 'full'
                          ? 'bg-green-700 text-white'
                          : 'bg-surface-inset text-secondary hover:bg-surface-hover'
                      }`}
                    >
                      {t('detail.payment.paymentTypeFull')}
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePaymentTypeChange('partial')}
                      className={`flex-1 py-2 px-4 rounded-md ${
                        paymentType === 'partial'
                          ? 'bg-yellow-700 text-white'
                          : 'bg-surface-inset text-secondary hover:bg-surface-hover'
                      }`}
                    >
                      {t('detail.payment.paymentTypePartial')}
                    </button>
                  </div>
                </div>
                
                {/* Zahlungsbetrag */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-secondary mb-2" htmlFor="payment-amount">
                    {t('detail.payment.amount')}
                  </label>
                  <div className="relative">
                    <input
                      id="payment-amount"
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={paymentType === 'partial' ? selectedInvoice.totalAmount : undefined}
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      className="block w-full rounded-md bg-surface-inset border-strong text-primary py-2 px-3 focus:border-indigo-500 focus:ring-indigo-500"
                      required
                      disabled={paymentLoading}
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <span className="text-muted">{selectedInvoice.currency}</span>
                    </div>
                  </div>
                </div>
                
                {/* Zahlungsdatum */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-secondary mb-2" htmlFor="payment-date">
                    {t('detail.payment.date')}
                  </label>
                  <input
                    id="payment-date"
                    type="date"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    className="block w-full rounded-md bg-surface-inset border-strong text-primary py-2 px-3 focus:border-indigo-500 focus:ring-indigo-500"
                    required
                    disabled={paymentLoading}
                  />
                </div>
                
                {/* Notiz */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-secondary mb-2" htmlFor="payment-note">
                    {t('detail.payment.note')}
                  </label>
                  <textarea
                    id="payment-note"
                    value={paymentNote}
                    onChange={(e) => setPaymentNote(e.target.value)}
                    className="block w-full rounded-md bg-surface-inset border-strong text-primary py-2 px-3 focus:border-indigo-500 focus:ring-indigo-500"
                    rows={2}
                    placeholder="z.B. Zahlungsreferenz"
                    disabled={paymentLoading}
                  />
                </div>
                
                {/* Aktionen */}
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closePaymentModal}
                    className="px-4 py-2 border border-strong rounded-md text-secondary hover:bg-surface-inset transition-colors"
                    disabled={paymentLoading}
                  >
                    {t('detail.payment.cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={confirmPayment}
                    className={`px-4 py-2 rounded-md flex items-center ${
                      paymentType === 'full'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-yellow-600 hover:bg-yellow-700'
                    } text-white transition-colors ${
                      !paymentAmount || paymentLoading ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    disabled={!paymentAmount || paymentLoading}
                  >
                    {paymentLoading && <LoadingSpinner size={20} />}
                    {t('detail.payment.register')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Down Payment Modal */}
        {showDownPaymentModal && selectedDocumentForDownPayment && (
          <DownPaymentModal
            isOpen={showDownPaymentModal}
            onClose={closeDownPaymentModal}
            baseDocument={{
              id: selectedDocumentForDownPayment.id,
              type: selectedDocumentForDownPayment.type as 'offer' | 'invoice',
              totalAmount: selectedDocumentForDownPayment.totalAmount,
              currency: selectedDocumentForDownPayment.currency,
              documentNumber: selectedDocumentForDownPayment.number || 'N/A'
            }}
            existingDownPayments={existingDownPayments}
            onDownPaymentCreated={handleDownPaymentCreated}
            format="zugferd"
          />
        )}

        {/* Dokument-Status-Modal */}
        {documentToUpdate && documentNewStatus && (
          <div className="fixed inset-0 bg-surface bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-surface-raised rounded-lg shadow-xl border border-hairline w-full max-w-md sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h3 className="text-xl font-semibold text-primary mb-4">
                  {t('detail.statusUpdate.title')}
                </h3>
                
                <div className="mb-4">
                  <p className="text-secondary mb-2">
                    {t('detail.statusUpdate.confirmChange', { status: getStatusTranslation(documentNewStatus) })}
                  </p>
                  
                  {workflow?.documents?.find(d => d.id === documentToUpdate.id) && (
                    <div className="text-secondary mb-4">
                      <p className="font-medium">{getDocumentTypeName({ type: documentToUpdate.type } as WorkflowDocument)} #{workflow.documents.find(d => d.id === documentToUpdate.id)?.number}</p>
                      <p className="text-sm text-muted">
                        {t('detail.common.currentStatus')}: <span className={`px-1.5 py-0.5 text-xs rounded ${getStatusClass(String(workflow.documents.find(d => d.id === documentToUpdate.id)?.status || ''))}`}>
                          {getStatusTranslation(String(workflow.documents.find(d => d.id === documentToUpdate.id)?.status || ''))}
                        </span>
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-secondary mb-1">
                    {t('detail.statusUpdate.comment')}
                  </label>
                  <textarea
                    value={documentStatusComment}
                    onChange={(e) => setDocumentStatusComment(e.target.value)}
                    className="block w-full rounded-md bg-surface-inset border border-strong py-2 px-3 focus:border-indigo-500 focus:ring-indigo-500 text-primary"
                    rows={2}
                    placeholder={t('detail.statusUpdate.commentPlaceholder')}
                    disabled={documentStatusLoading}
                  />
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setDocumentToUpdate(null);
                      setDocumentNewStatus('');
                      setDocumentStatusComment('');
                    }}
                    className="px-4 py-2 border border-strong rounded-md text-secondary hover:bg-surface-inset transition-colors"
                    disabled={documentStatusLoading}
                  >
                    {t('detail.common.cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (process.env.NODE_ENV === 'development') {
                        console.log('[WorkflowDetailPage] Status aktualisieren geklickt', {
                          documentId: documentToUpdate.id,
                          documentType: documentToUpdate.type,
                          newStatus: documentNewStatus
                        });
                      }
                      handleUpdateDocumentStatus();
                    }}
                    className={`px-4 py-2 rounded-md flex items-center ${
                      documentNewStatus && !documentStatusLoading
                        ? 'bg-indigo-600 hover:bg-indigo-700 text-[#0C1322]'
                        : 'bg-surface-inset text-muted cursor-not-allowed'
                    } transition-colors`}
                    disabled={!documentNewStatus || documentStatusLoading}
                  >
                    {documentStatusLoading && <LoadingSpinner size={20} />}
                    {t('detail.statusUpdate.save')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkflowDetailPage; 